import React, { useEffect, useState } from "react"; //useContext,
import {
  Avatar,
  styled,
  Tooltip,
  Stack,
  Typography,
  Link,
  FormControl,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  Checkbox,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridFooterContainer,
  GridPagination,
  useGridApiRef,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiContext,
  useGridSelector,
  gridPaginatedVisibleSortedGridRowIdsSelector,
  //  GridRowParams,
} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import CustomNoRowsOverlay from "./gridNoRowOverLay";
import AssignmentLateTwoToneIcon from "@mui/icons-material/AssignmentLateTwoTone";
import AssignmentTurnedInTwoToneIcon from "@mui/icons-material/AssignmentTurnedInTwoTone";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import moment from "moment";
import EditNoteIcon from "@mui/icons-material/EditNote";
import VerifiedIcon from "@mui/icons-material/Verified";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";
import JBWAgreementsDetailDialog from "./JBWAgreementsDetailDialog";
import InvoiceDetailsDialog from "./InvoiceDetailsDialog";
import JBWClaimsIframDialog from "./JBWClaimsIframDialog";
import { envConfig } from "../services/configs/envConfig";
//import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteConfirmDialog } from "./EquipmentsTable";
//import { rejectClaim } from "../services/ClaimsServices";
//import { AuthContext } from "../AuthContext";
import JBWDateLabel from "./JBWDateLabel";
import { useLocation } from "react-router-dom";
import CreateClaimDialog from "./CreateClaimDialog";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import { getFilteredGridRows } from "../utils/gridExport";
import exportFromJSON from "export-from-json";
import { hasBusinessUnitData } from "../utils/businessUnit";
import { csvSafe } from "../utils/csv";
import { initialFilter } from "../utils/deepLink";
import { invoiceLabel, invoiceUrls } from "../utils/invoiceLinks";
import EnterpriseHubLink from "./EnterpriseHubLink";

// Page-scoped "select all".
//
// The free DataGrid FORCES `checkboxSelectionVisibleOnly: false` in its
// DATA_GRID_FORCED_PROPS (node_modules/@mui/x-data-grid/DataGrid/useDataGridProps.js:15) --
// the prop is Pro/Premium-only and is silently ignored here. So the built-in header
// checkbox selects every filtered row across every page, which is what made "Ignore
// Selected Claims" act on the whole list instead of the page.
//
// Replacing the header cell is the supported way to fix this on the free grid. Row ids come
// from gridPaginatedVisibleSortedGridRowIdsSelector, so this follows the CURRENT page after
// sorting and quick-filtering -- deriving them from claimsList/page/pageSize would go wrong
// the moment the user sorts or searches.
const PageSelectAllCheckbox = () => {
  const apiRef = useGridApiContext();
  const pageRowIds = useGridSelector(
    apiRef,
    gridPaginatedVisibleSortedGridRowIdsSelector
  );

  // Respect isRowSelectable: already-claimed rows must never be swept in.
  const selectableIds = React.useMemo(
    () =>
      pageRowIds.filter((id) => {
        const row = apiRef.current.getRow(id);
        return row && !row.jbwClaimId;
      }),
    [apiRef, pageRowIds]
  );

  const selectionModel = useGridSelector(apiRef, (state) => state.rowSelection);
  const selectedOnPage = selectableIds.filter((id) =>
    selectionModel.includes(id)
  ).length;

  const allSelected =
    selectableIds.length > 0 && selectedOnPage === selectableIds.length;
  const someSelected = selectedOnPage > 0 && !allSelected;

  const handleChange = (event) => {
    const pageSet = new Set(selectableIds);
    // Preserve selections made on other pages; only this page's rows are toggled.
    const others = selectionModel.filter((id) => !pageSet.has(id));
    apiRef.current.setRowSelectionModel(
      event.target.checked ? [...others, ...selectableIds] : others
    );
  };

  return (
    <Checkbox
      checked={allSelected}
      indeterminate={someSelected}
      disabled={selectableIds.length === 0}
      onChange={handleChange}
      inputProps={{ "aria-label": "Select all rows on this page" }}
    />
  );
};

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .super-app-theme--Active": {
    backgroundColor: "#71d68e",
    "&:hover": {
      backgroundColor: "#4ECC72",
    },
  },
  "& .super-app-theme--InActive": {
    backgroundColor: "#e4c553",
    "&:hover": {
      backgroundColor: "#DEB729",
    },
  },
}));

const QuickSearchToolbar = ({
  filterStatus,
  handleFilterChange,
  pageSize,
  handlePageSizeChange,
  quickFilterActive,
  claimsList,
  isAnyRowSelected,
  selectedClaims,
  handleMultiRevive,
  handleMultiDelete,
  setOpenCreateClaimDialog,
  handleExport,
}) => {
  return (
    <GridToolbarContainer
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {/* Left-aligned Quick Filter */}
      <Box
        sx={{
          p: 0.5,
          pb: 0,
        }}
      >
        <GridToolbarQuickFilter />
      </Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle1">Filter by:</Typography>
        <FormControl sx={{ minWidth: 180 }}>
          <Select
            value={filterStatus}
            onChange={handleFilterChange}
            size="small"
            color="warning"
          >
            <MenuItem value="Potential Claims">Potential Claims</MenuItem>
            <MenuItem value="Processing">Processing</MenuItem>
            <MenuItem value="Missing Documents">Missing Documents</MenuItem>
            <MenuItem value="Denied">Denied</MenuItem> {/* declined to denied*/}
            <MenuItem value="Revive">Revive</MenuItem>
            <MenuItem value="Approved and Paid">Approved and Paid</MenuItem>
            <MenuItem value="All">All</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          sx={{ borderRadius: "50px" }}
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setOpenCreateClaimDialog(true)}
        >
          Claim
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={claimsList.length === 0}
        >
          Export ({quickFilterActive ? "Search Results" : filterStatus})
        </Button>
        {isAnyRowSelected &&
          (filterStatus === "Revive" ? (
            <Button
              variant="contained"
              color="success"
              onClick={() => handleMultiRevive(selectedClaims)}
            >
              Revive Selected Claims
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={handleMultiDelete}
            >
              Ignore Selected Claims
            </Button>
          ))}
      </Box>

      {/* Right-aligned Page Size Control */}
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle1">Page Size:</Typography>
        <FormControl sx={{ minWidth: 75 }}>
          <Select
            labelId="page-size-label"
            value={pageSize}
            onChange={handlePageSizeChange}
            sx={{
              border: "none",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
          >
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </GridToolbarContainer>
  );
};

const CustomFooter = ({
  isAnyRowSelected,
  filterStatus,
  handleMultiRevive,
  selectedClaims,
  handleMultiDelete,
}) => (
  <GridFooterContainer
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    {/* Left-aligned Delete Button */}
    <Box sx={{ padding: "10px" }}>
      {isAnyRowSelected &&
        (filterStatus === "Revive" ? (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleMultiRevive(selectedClaims)}
          >
            Revive Selected Claims
          </Button>
        ) : (
          <Button variant="contained" color="error" onClick={handleMultiDelete}>
            Ignore Selected Claims
          </Button>
        ))}
    </Box>

    {/* Right-aligned Pagination */}
    <GridPagination />
  </GridFooterContainer>
);

const ClaimsGrid = ({
  claimList,
  showDealer = false,
  showLoading = false,
  dealer,
  onRejectClaim,
  onVerifyClaim,
  onUpdateClaim,
  onReviveClaim,
  onEditAgreement,
  tenant,
  businessUnits,
  handleMultiRevive,
  onRefreshClaims,
}) => {
  const [selectedAgreementId, setSelectedAgreementId] = useState();
  const [openAgreementDetailDialog, setOpenAgreementDetailDialog] =
    useState(false);
  // const { user } = useContext(AuthContext);
  const location = useLocation();
  const [selectedInvoiceId, setSelectedInvoiceId] = useState();
  const [openInvoiceDetailDialog, setOpenInvoiceDetailDialog] = useState(false);
  const [claimAgreementId, setClaimAgreementId] = useState();
  const [claimDealerId, setClaimDealerId] = useState();
  const [selectedClaim, setSelectedClaim] = useState();
  const [claimsList, setClaimsList] = useState([]);
  // Navigation state: Dealer Summary's, or a link DealerDetails turned into state (#419).
  const [filterStatus, setFilterStatus] = useState(() =>
    initialFilter(location, "claims", "Potential Claims"),
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [claimIframeContext, setClaimIframeContext] = useState(null);

  const [openJBWIframeClaimDialog, setOpenJBWIframeClaimDialog] =
    useState(false);

  const [selectedClaims, setSelectedClaims] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [claimToDelete, setClaimToDelete] = useState(null);
  const [isAnyRowSelected, setIsAnyRowSelected] = useState(false);
  const [openCreateClaimDialog, setOpenCreateClaimDialog] = useState(false);
  const [quickFilterActive, setQuickFilterActive] = useState(false);
  const [noAgreementWarning, setNoAgreementWarning] = useState(false);
  const apiRef = useGridApiRef();

  // The loading skeleton unmounts the DataGrid; the remounted grid starts with nothing selected
  // and fires no selection event, so clear the mirrored state or the multi-action buttons go stale.
  useEffect(() => {
    if (showLoading) {
      setSelectedClaims([]);
      setIsAnyRowSelected(false);
    }
  }, [showLoading]);

  const columns = [
    {
      // Same field as the built-in checkbox column, so this definition replaces it and the
      // header renders our page-scoped select-all instead of the grid's select-everything.
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      renderHeader: () => <PageSelectAllCheckbox />,
    },
    {
      field: "id",
      headerName: "",
      width: 45,
      sortable: false,
      renderCell: (params) => {
        const claimId = params.row.jbwClaimId;
        const bgColor =
          claimId === null || claimId === "" ? "#DEB729" : "#4ECC72";
        const iconDisaply =
          claimId === null || claimId === "" ? (
            <AssignmentLateTwoToneIcon />
          ) : (
            <AssignmentTurnedInTwoToneIcon />
          );
        return (
          <Tooltip
            title={`JB360# :${params.row.id}-Claim Id :${params.row.jbwClaimId}`}
            arrow
          >
            <Avatar sx={{ bgcolor: bgColor, m: 1 }}>{iconDisaply}</Avatar>
          </Tooltip>
        );
      },
      headerAlign: "center",
      align: "center",
    },
    {
      field: "jbwAgreementId",
      headerName: "JBW Agreement ID",
      width: 220,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const { value, row } = params;
        const claimId = row.jbwClaimId;
        const baseUrl =
          `${envConfig.JBWLegacyUrl}/member/service/customeragpdf.aspx?agreementid=`;

        const agreementPurchaseDate = row.agreementPurchaseDate
          ? moment.utc(row.agreementPurchaseDate).format("MM/DD/YYYY")
          : "NA";

        const warrantyStartDate = row.warrantyStartDate
          ? moment.utc(row.warrantyStartDate).format("MM/DD/YYYY")
          : "NA";

        const warrantyEndDate = row.warrantyEndDate
          ? moment.utc(row.warrantyEndDate).format("MM/DD/YYYY")
          : "NA";

        // Split values
        const items = value ? value.split(",").map((v) => v.trim()) : [];

        // Truncate logic (show 2 + ...)
        const visibleItems =
          items.length > 3 ? [...items.slice(0, 2), "..."] : items;

        // Tooltip content (clickable list + extra info)
        const tooltipContent = (
          <div>
            {/* INLINE CLICKABLE IDS */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              {items.length > 0 ? (
                items.map((item, i) => (
                  <a
                    key={i}
                    href={`${baseUrl}${item}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      color: "#90caf9", // lighter blue (visible on dark bg)
                      background: "rgba(255,255,255,0.08)",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      textDecoration: "none",
                      fontWeight: 500,
                    }}
                  >
                    {item}
                  </a>
                ))
              ) : (
                <span>N/A</span>
              )}
            </div>

            {/* EXTRA INFO */}
            <div>{`Agreement Purchase Date : ${agreementPurchaseDate}`}</div>
            <div>{`Warranty Start Date : ${warrantyStartDate}`}</div>
            <div>{`Warranty End Date : ${warrantyEndDate}`}</div>
          </div>
        );

        return (
          <Box display="flex" alignItems="center" width="100%">
            {/* CLICKABLE IDS */}
            <Tooltip title={tooltipContent} arrow>
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {visibleItems.length > 0 ? (
                  visibleItems.map((item, index) =>
                    item === "..." ? (
                      <span key={index}> ...</span>
                    ) : (
                      <Link
                        key={index}
                        href={`${baseUrl}${item}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="always"
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginRight: 4 }}
                      >
                        {item}
                        {index < visibleItems.length - 1 &&
                        visibleItems[index + 1] !== "..."
                          ? ","
                          : ""}
                      </Link>
                    ),
                  )
                ) : (
                  <span>N/A</span>
                )}
              </Box>
            </Tooltip>

            {/* EDIT ICON */}
            {claimId === null && (
              <Tooltip title={`Edit Agreement Link`}>
                <EditIcon
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    ml: 1,
                    flexShrink: 0,
                    fontSize: 18,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditAgreement(row);
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "productCode",
      headerName: "Plan/Job #",
      width: showDealer ? 120 : 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const proCode = params.row.productCode;
        const crmSystem = dealer?.crmSystem;
        const { ehUrl } = invoiceUrls(params.row, crmSystem);
        return (
          <Stack>
            <Typography variant="string" component="span">
              {proCode}
            </Typography>
            <Typography variant="string" component="span">
              <Link className="table-cell-trucate" underline="always">
                {invoiceLabel(params.row.jobNumber, params.row.invoiceId, crmSystem)}
              </Link>
              <EnterpriseHubLink href={ehUrl} />
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice #",
      width: showDealer ? 100 : 110,
      sortable: true,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "serviceStartDate",
      headerName: "Invoice Date",
      width: showDealer ? 100 : 105,
      sortable: true,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => {
        if (param.value === null) return "";
        return (
          <JBWDateLabel inputDate={param.value} dateFormat="MMM.DD.YYYY" />
        );
      },
    },
    {
      field: "customerName",
      headerName: "Customer Details",
      width: showDealer ? 200 : 210,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const custName = params.row.customerName;
        const loc = params.row.location;
        return (
          <Stack>
            <Typography variant="string" component="span">
              {custName}
            </Typography>
            <Typography variant="string" component="span">
              {loc}
            </Typography>
          </Stack>
        );
      },
    },
    // {
    //   field: "location",
    //   headerName: "Location",
    //   width: showDealer ? 200 : 210,
    //   sortable: false,
    //   headerAlign: "center",
    //   align: "center",
    // },
    {
      field: "jbwClaimId",
      headerName: "Claim Id",
      width: showDealer ? 100 : 110,
      sortable: true,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "invoiceSummary",
      headerName: "Invoice Summary",
      width: showDealer ? 190 : 200,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "claimStatus",
      headerName: "Claim Status",
      width: showDealer ? 140 : 120,
      sortable: true,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      cellClassName: "actions",
      width: 80,
      headerAlign: "center",
      align: "center",
      getActions: (params) => {
        //console.log("params", params.row);
        const isClaimFiled = params.row.isClaimFiled;
        const ignoreInvoice = params.row.ignoreInvoice;
        const claimStatus = params.row.claimStatus;

        if (
          claimStatus?.toLowerCase() === "denied" ||
          claimStatus?.toLowerCase() === "approved and paid"
        ) {
          return [];
        }
        let cellItems = [];
        if (ignoreInvoice === 1) {
          cellItems.push(
            <GridActionsCellItem
              icon={<FlipCameraAndroidIcon fontSize="medium" />}
              label="Revive Claim"
              className="textPrimary"
              onClick={() => onReviveClaim(params.row)}
              color="secondary"
              title="Revive Claim"
              disabled={isAnyRowSelected}
            />,
          );
        }
        //isreadyfor jwd = 0
        if (ignoreInvoice !== 1) {
          if (isClaimFiled === 0) {
            cellItems.push(
              <GridActionsCellItem
                icon={<EditNoteIcon fontSize="medium" />}
                label="Create Claim"
                className="textPrimary"
                onClick={handleCreateClaim(params)}
                color="secondary"
                title="Create Claim"
                disabled={isAnyRowSelected}
              />,
            );
            cellItems.push(
              <GridActionsCellItem
                icon={<UnpublishedIcon fontSize="medium" />}
                label="Reject Claim"
                className="textPrimary"
                onClick={handleRejectClaim(params)}
                color="error"
                title="Reject Claim"
                disabled={isAnyRowSelected}
              />,
            );
          } else {
            cellItems.push(
              <GridActionsCellItem
                icon={<VerifiedIcon fontSize="medium" />}
                label="Check Claim Status"
                className="textPrimary"
                onClick={handleVerifyClaim(params)}
                color="primary"
                title="Check Claim Status"
                disabled={isAnyRowSelected}
              />,
            );
          }
        }

        return cellItems;
      },
      sortable: false,
    },
  ];

  if (hasBusinessUnitData(claimsList)) {
    columns.splice(
      columns.findIndex((column) => column.field === "jbwClaimId"),
      0,
      {
        field: "businessUnitName",
        headerName: "Business Unit",
        width: showDealer ? 150 : 160,
        sortable: true,
        headerAlign: "center",
        align: "center",
      }
    );
  }

  if (showDealer) {
    columns.splice(1, 0, {
      field: "dealerId",
      headerName: "Dealer ID",
      width: 90,
      sortable: false,
      headerAlign: "center",
      align: "center",
    });
  }

  // const handleEdit = (param, optype) => () => {
  //   console.log("Edit clicked ", param.row);
  //   //openEquimentDialog(param.row, optype);
  // };

  const handleMultiDelete = () => {
    setClaimToDelete(selectedClaims);
    setDeleteDialogOpen(true);
    // setClaimsList((prev) => prev.filter((claim) => !selectedClaims.includes(claim.id)));
    console.log(selectedClaims);
    // setSelectedClaims([]);
  };

  const handleRowSelection = (selectedIds) => {
    const selectedRows = claimsList.filter((claim) =>
      selectedIds.includes(claim.id),
    );
    setSelectedClaims(selectedRows);
    setIsAnyRowSelected(selectedIds.length > 0);
    console.log("Selected Rows Data: ", selectedRows);
    // Now you have the selected row data; you can use it as needed.
  };

  const handleRejectClaim = (params) => () => {
    setClaimToDelete(params.row);
    setDeleteDialogOpen(true);
    //onRejectClaim(params.row);
  };
  const handleVerifyClaim = (params) => () => {
    onVerifyClaim(params.row);
  };

  const handleCellClick = (params) => {
    //console.log("cell click event :", params);
    const columnName = params.field;
    //const cellValue = params.value;
    //console.log(`Selected column : ${columnName} and cell value ${cellValue}`);
    if (columnName === "jbwAgreementId") {
      const agreementLink = params.row.agreementlink;
      if (!agreementLink) return;
      window.open(agreementLink, "_blank");
    } else if (columnName === "productCode") {
      const invoiceUrl = invoiceUrls(params.row, dealer.crmSystem).url;
      console.log("invoice st url :", invoiceUrl);
      window.open(invoiceUrl, "_blank");
    }
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
    setPage(0);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value); // Update page size
    setPage(0); // Reset to the first page when page size changes
  };

  const handleAgreementDetailDialogClose = () => {
    setOpenAgreementDetailDialog(false);
  };

  const handleInvoiceDetailDialogClose = () => {
    setSelectedInvoiceId("");
    setOpenInvoiceDetailDialog(false);
  };

  const handleJBWIframeClaimDialogClose = (param) => {
    console.log("claim grid : ", param);
    setClaimDealerId();
    setClaimAgreementId();
    setClaimIframeContext(null);
    onUpdateClaim({ ...selectedClaim, ...param });
    setOpenJBWIframeClaimDialog(false);
  };

  const handleCreateClaim = (params) => () => {
    console.log("selected cliam create ", params);
    if (!params.row.jbwAgreementId) {
      setNoAgreementWarning(true);
      return;
    }

    setSelectedClaim(params.row);
    setClaimDealerId(params.row.dealerId);
    console.log(
      "Create claim selected Agreement id :",
      params.row.jbwAgreementId,
    );
    setClaimAgreementId(params.row.jbwAgreementId);
    console.log("Create claim selected job Number :", params.row.jobNumber);
    console.log("Create claim selected Invoice ID :", params.row.invoiceId);
    setClaimIframeContext({
      dealerId: params.row.dealerId,
      agreementId: params.row.jbwAgreementId,
      jobNumber: params.row.jobNumber,
      claim: params.row,
    });
    setOpenJBWIframeClaimDialog(true);
  };

  const handleDialogClose = (data) => {
    setDeleteDialogOpen(false);
    console.log("equipment delete index : ", data);
    if (data) {
      onRejectClaim(claimToDelete);
    }
  };

  const handleRefreshClaims = () => {
    setOpenCreateClaimDialog(false);

    if (typeof onUpdateClaim === "function") {
      onUpdateClaim();
    }
  };

  useEffect(() => {
    let filteredList = claimList;

    if (tenant) {
      filteredList = claimList.filter((cl) => cl.tenantId === tenant);
    }
    if (businessUnits && businessUnits.length > 0) {
      console.log("business units :", businessUnits);
      filteredList = filteredList.filter((fl) =>
        businessUnits.includes(fl.businessUnitId),
      );
      console.log("Claims with filtered business units :", filteredList);
    } else {
      console.log("No Business units filtered :", businessUnits);
    }

    if (filterStatus === "Potential Claims") {
      filteredList = filteredList.filter(
        (cl) => !cl.jbwClaimId && cl.ignoreInvoice === 0,
      );
    } else if (filterStatus === "Processing") {
      filteredList = filteredList.filter(
        (cl) => cl.claimStatus?.toLowerCase() === "processing",
      );
    } else if (filterStatus === "Missing Documents") {
      filteredList = filteredList.filter(
        (cl) => cl.claimStatus?.toLowerCase() === "missing documents",
      );
    } else if (filterStatus === "Denied") {
      filteredList = filteredList.filter(
        (cl) => cl.claimStatus?.toLowerCase() === "denied",
      ); // declined to denied
    } else if (filterStatus === "Approved and Paid") {
      filteredList = filteredList.filter(
        (cl) => cl.claimStatus?.toLowerCase() === "approved and paid",
      );
    } else if (filterStatus === "Revive") {
      filteredList = filteredList.filter((cl) => cl.ignoreInvoice === 1);
    }
    setClaimsList(filteredList);
    // setPage(0);
  }, [filterStatus, tenant, claimList, businessUnits]);

  const handleExport = () => {
    const isSearchActive = quickFilterActive;
    // A selection is an explicit narrowing by the user, so it wins over the filter/search
    // view -- exporting all 552 filtered rows while 25 are ticked is not what ticking them
    // meant. With nothing selected, export the current view exactly as before.
    // Read the grid's live selection, never the selectedClaims state: that state goes stale
    // when the grid remounts (loading skeleton) or a per-row Ignore sets it without ticking.
    const selectedRows = [...apiRef.current.getSelectedRows().values()];
    const hasSelection = selectedRows.length > 0;
    // The grid hides the Business Unit column when no row resolved a name, but
    // the CSV always carries it so every claims export has the same shape. Dash
    // rather than blank, so an empty cell reads as "no business unit" instead of
    // as a truncated file.
    const exportData = (
      hasSelection
        ? selectedRows
        : isSearchActive
          ? getFilteredGridRows(apiRef)
          : claimsList
    ).map((row) => ({
      ...row,
      // Every free-text column here is ServiceTitan-sourced and unvalidated, so they are all
      // neutralised rather than just the new one -- escaping businessUnitName alone would imply
      // its neighbours were already safe.
      customerName: csvSafe(row.customerName),
      location: csvSafe(row.location),
      invoiceSummary: csvSafe(row.invoiceSummary),
      productCode: csvSafe(row.productCode),
      businessUnitName: csvSafe(row.businessUnitName) || "-",
    }));
    const label = hasSelection
      ? "selected"
      : isSearchActive
        ? "search"
        : filterStatus;
    const fileName = `claims-${label}-${new Date().getTime()}`;
    const fields = [
      "id",
      "dealerId",
      "jbwAgreementId",
      "productCode",
      "invoiceId",
      "jobNumber",
      "customerName",
      "location",
      "businessUnitName",
      "jbwClaimId",
      "invoiceSummary",
      "claimStatus",
      "claimAmount",
      "paidAmount",
      "serviceStartDate",
    ];
    exportFromJSON({
      data: exportData,
      fileName,
      fields,
      exportType: exportFromJSON.types.csv,
    });
  };

  return (
    <>
      {/* <Box sx={{ mb: 2 }}>
        {isAnyRowSelected && (
          <Button variant="contained" color="error" onClick={handleMultiDelete}>
            Ignore Selected Claims
          </Button>
        )}
      </Box> */}
      <Snackbar
        open={noAgreementWarning}
        autoHideDuration={4000}
        onClose={() => setNoAgreementWarning(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setNoAgreementWarning(false)}>
          Cannot create claim: no JBW Agreement ID is linked to this record.
        </Alert>
      </Snackbar>
      <CreateClaimDialog
        open={openCreateClaimDialog}
        onClose={() => {
          setOpenCreateClaimDialog(false);
          onRefreshClaims?.(); // refresh when dialog closes
        }}
        dealer={dealer}
        tenant={tenant}
      />
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDialogClose}
        title="Ignore Claims"
        message="Are You Sure. Do you want to ignore the selected claims?"
      />
      {claimIframeContext && (
        <JBWClaimsIframDialog
          dialogOpen={openJBWIframeClaimDialog}
          onClose={handleJBWIframeClaimDialogClose}
          claimIframeContext={claimIframeContext}
        />
      )}

      {selectedAgreementId && (
        <JBWAgreementsDetailDialog
          jbwAgreementId={selectedAgreementId}
          dialogOpen={openAgreementDetailDialog}
          onClose={handleAgreementDetailDialogClose}
        />
      )}
      {selectedInvoiceId && (
        <InvoiceDetailsDialog
          invoiceId={selectedInvoiceId}
          dialogOpen={openInvoiceDetailDialog}
          onClose={handleInvoiceDetailDialogClose}
        />
      )}
      {showLoading === true ? (
        <Box sx={{ height: "100px" }}>
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
        </Box>
      ) : (
        <StyledDataGrid
          apiRef={apiRef}
          rows={claimsList}
          onCellClick={handleCellClick}
          getRowId={(row) => row.id}
          columns={columns}
          autoHeight
          getRowHeight={() => "auto"}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => setPage(model.page)}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
          }}
          checkboxSelection
          isRowSelectable={(params) => !params.row.jbwClaimId}
          onRowSelectionModelChange={(selectionModel) => {
            // handleRowSelection stores the row objects; consumers (ClaimsList
            // handleRejectClaim / handleMultiRevive) read .id off them.
            handleRowSelection(selectionModel);
          }}
          disableColumnFilter={true}
          disableColumnMenu={true}
          onFilterModelChange={(model) => {
            const quickFilterValues = model.quickFilterValues || [];
            const hasQuickFilter =
              quickFilterValues.length > 0 &&
              quickFilterValues.some((v) => v?.trim()?.length > 0);

            setQuickFilterActive(hasQuickFilter);
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: QuickSearchToolbar,
            footer: CustomFooter,
          }}
          slotProps={{
            toolbar: {
              filterStatus,
              handleFilterChange,
              pageSize,
              handlePageSizeChange,
              quickFilterActive,
              claimsList,
              isAnyRowSelected,
              selectedClaims,
              handleMultiRevive,
              handleMultiDelete,
              setOpenCreateClaimDialog,
              handleExport,
            },
            footer: {
              isAnyRowSelected,
              filterStatus,
              handleMultiRevive,
              selectedClaims,
              handleMultiDelete,
            },
          }}
          sx={{
            "& .muidatagrid-colcelltitle ": {
              display: "block",
              textAlign: "center",
              width: "100%",
            },
            "& .MuiTablePagination-toolbar .MuiTablePagination-selectLabel": {
              display: "none", // Hides the "Rows per page" label
            },
            "& .MuiTablePagination-toolbar .MuiInputBase-root": {
              display: "none", // Hides the dropdown
            },
          }}
        />
      )}
    </>
  );
};

export default ClaimsGrid;
